import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { LogOut, ExternalLink, Loader2, Menu, X } from "lucide-react";
import { api } from "@/lib/api";
import { ContentTab } from "@/components/admin/ContentTab";
import { SettingsTab } from "@/components/admin/SettingsTab";
import { IntegrationsTab } from "@/components/admin/IntegrationsTab";
import { SectionsTab } from "@/components/admin/SectionsTab";
import { PagesTab } from "@/components/admin/PagesTab";
import { EnquiriesTab } from "@/components/admin/EnquiriesTab";
import { AssetsTab } from "@/components/admin/AssetsTab";
import { PostsTab } from "@/components/admin/PostsTab";
import { AnalyticsTab } from "@/components/admin/AnalyticsTab";

const CONTENT_TABS = {
  stats: {
    fields: [
      { name: "value", label: "Value (e.g. 300K+)" },
      { name: "label", label: "Label" },
    ],
    titleKey: "value",
    subtitleKey: "label",
  },
  services: {
    fields: [
      { name: "title", label: "Service title (must match ProPract name to merge details)" },
      { name: "price", label: "Price (e.g. ₹2,500)" },
      { name: "duration", label: "Duration" },
      { name: "mode", label: "Mode (clinic / online)" },
      { name: "desc", label: "Short description", type: "textarea" },
      { name: "why", label: "Why book this (detail block)", type: "textarea" },
      { name: "who", label: "Who should book (detail block)", type: "textarea" },
    ],
    subtitleKey: "price",
  },
  courses: {
    fields: [
      { name: "title", label: "Course title (must match ProPract name to merge details)" },
      { name: "tagline", label: "Tagline" },
      { name: "modules", label: "Modules", type: "number" },
      { name: "hours", label: "Hours (e.g. 6.5 hrs)" },
      { name: "price", label: "Price" },
      { name: "kind", label: "Type override: course or event (optional)" },
      { name: "why", label: "Why buy (detail block)", type: "textarea" },
      { name: "who", label: "Who should buy (detail block)", type: "textarea" },
    ],
    subtitleKey: "price",
  },
  podcasts: {
    fields: [
      { name: "title", label: "Episode title" },
      { name: "show", label: "Show / podcast name" },
      { name: "youtube_id", label: "YouTube link (paste full URL or video ID)" },
      { name: "thumbnail", label: "Thumbnail image", type: "image" },
      { name: "release_date", label: "Release date (for upcoming)", type: "date" },
      { name: "description", label: "Description", type: "textarea" },
      { name: "upcoming", label: "Mark as upcoming episode", type: "checkbox" },
    ],
    subtitleKey: "show",
  },
  reels: {
    fields: [
      { name: "img", label: "Thumbnail image", type: "image" },
      { name: "caption", label: "Caption" },
      { name: "url", label: "Instagram reel URL" },
      { name: "pinned", label: "Pin as Top reel", type: "checkbox" },
    ],
    titleKey: "caption",
  },
  marquee: {
    fields: [{ name: "text", label: "Marquee phrase" }],
    titleKey: "text",
  },
  about: {
    fields: [
      { name: "title", label: "Chapter title" },
      { name: "body", label: "Chapter text", type: "textarea" },
    ],
    subtitleKey: "body",
  },
  faqs: {
    fields: [
      { name: "question", label: "Question" },
      { name: "article_slug", label: "Linked article slug (optional — adds a Read-more link)" },
      { name: "answer", label: "Short answer", type: "textarea" },
    ],
    titleKey: "question",
  },
  stories: {
    fields: [
      { name: "name", label: "Mother's name" },
      { name: "tag", label: "Tag (e.g. Lactation consult · Gurgaon)" },
      { name: "quote", label: "Testimonial", type: "textarea" },
    ],
    titleKey: "name",
    subtitleKey: "tag",
  },
};

const TABS = ["analytics", "settings", "integrations", "sections", "stats", "services", "courses", "podcasts", "reels", "stories", "faqs", "marquee", "about", "posts", "assets", "pages", "enquiries"];

export default function AdminDashboard() {
  const [user, setUser] = useState(null);
  const [checking, setChecking] = useState(true);
  const [tab, setTab] = useState("analytics");
  const [menuOpen, setMenuOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    api
      .get("/auth/me")
      .then((r) => setUser(r.data))
      .catch(() => navigate("/admin/login"))
      .finally(() => setChecking(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const logout = async () => {
    await api.post("/auth/logout").catch(() => {});
    navigate("/admin/login");
  };

  if (checking)
    return (
      <div className="flex min-h-screen items-center justify-center bg-sand">
        <Loader2 className="h-6 w-6 animate-spin text-terra" />
      </div>
    );
  if (!user) return null;

  const nav = (
    <nav className="mt-8 flex flex-col gap-1" data-testid="admin-tabs">
      {TABS.map((t) => (
        <button
          key={t}
          data-testid={`admin-tab-btn-${t}`}
          onClick={() => {
            setTab(t);
            setMenuOpen(false);
          }}
          className={`rounded-xl px-4 py-2.5 text-left font-outfit text-sm font-medium capitalize transition-colors ${
            tab === t ? "bg-ink text-sand" : "text-plumsoft hover:bg-cream"
          }`}
        >
          {t}
        </button>
      ))}
    </nav>
  );

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-sand lg:flex">
      {/* mobile top bar */}
      <header className="sticky top-0 z-40 flex h-16 items-center justify-between border-b border-ink/10 bg-sand/90 px-4 backdrop-blur-xl lg:hidden">
        <button data-testid="admin-menu-toggle" onClick={() => setMenuOpen(true)} aria-label="Open menu" className="text-ink">
          <Menu className="h-6 w-6" />
        </button>
        <span className="font-display text-lg font-semibold text-ink">
          The Birth <span className="italic text-terra">Life</span> · Admin
        </span>
        <a data-testid="admin-view-site" href="/" target="_blank" rel="noopener noreferrer" className="text-plumsoft">
          <ExternalLink className="h-5 w-5" />
        </a>
      </header>

      {/* mobile drawer overlay */}
      {menuOpen && (
        <div className="fixed inset-0 z-40 bg-ink/50 lg:hidden" onClick={() => setMenuOpen(false)} data-testid="admin-menu-overlay" />
      )}

      {/* left sidebar */}
      <aside
        data-testid="admin-sidebar"
        className={`fixed inset-y-0 left-0 z-50 flex w-64 flex-col border-r border-ink/10 bg-cream/70 p-5 transition-transform duration-300 lg:sticky lg:top-0 lg:h-screen lg:translate-x-0 ${
          menuOpen ? "translate-x-0" : "-translate-x-full"
        }`}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <img src="/assets/logo-circle.png" alt="" className="h-9 w-9 rounded-full" />
            <span className="font-display text-base font-semibold leading-tight text-ink">
              The Birth <span className="italic text-terra">Life</span>
              <span className="block font-outfit text-[10px] font-medium uppercase tracking-widest text-faint">Admin</span>
            </span>
          </div>
          <button className="lg:hidden" onClick={() => setMenuOpen(false)} aria-label="Close menu">
            <X className="h-5 w-5 text-faint" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto">{nav}</div>

        <div className="space-y-2 border-t border-ink/10 pt-4">
          <a data-testid="admin-view-site-side" href="/" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 rounded-xl px-4 py-2.5 font-outfit text-sm font-medium text-plumsoft hover:bg-cream">
            <ExternalLink className="h-4 w-4" /> View site
          </a>
          <button data-testid="admin-logout" onClick={logout} className="flex w-full items-center gap-2 rounded-xl px-4 py-2.5 text-left font-outfit text-sm font-medium text-plumsoft hover:bg-cream hover:text-terra">
            <LogOut className="h-4 w-4" /> Logout
          </button>
        </div>
      </aside>

      <main className="min-w-0 flex-1 px-4 py-8 sm:px-8 lg:px-12">
        {tab === "analytics" && <AnalyticsTab />}
        {tab === "settings" && <SettingsTab />}
        {tab === "integrations" && <IntegrationsTab />}
        {tab === "sections" && <SectionsTab />}
        {tab === "pages" && <PagesTab />}
        {tab === "enquiries" && <EnquiriesTab />}
        {tab === "assets" && <AssetsTab />}
        {tab === "posts" && <PostsTab />}
        {CONTENT_TABS[tab] && <ContentTab coll={tab} {...CONTENT_TABS[tab]} syncable={tab === "services" || tab === "courses"} />}
      </main>
    </div>
  );
}
